import React from 'react';
import Stack from 'react-bootstrap/Stack';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

import SetUpBridgeModal from './SetUpBridgeModal';
import ToastMessage from '../Widgets/ToastMessage';
import AntelopeTokenAPI from '../Helpers/AntelopeTokenAPI';
import EVMTokenAPI from '../Helpers/EVMTokenAPI';
import LoadingButton from '../Widgets/LoadingButton';
import commonContextsWrapper from '../Helpers/commonContextsWrapper';
import { parseUnits } from 'ethers';

import './IssueTokenForm.css';

class IssueTokenForm extends React.Component {
    get sharedState() {
        return this.props.sharedState?.[0];
    }

    get logout() {
        return this.props.sharedState?.[1]?.logout;
    }

    get updateSharedState() {
        return this.props.sharedState?.[1]?.updateState;
    }

    get isRunningOnEVM() {
        return !!this.sharedState?.evmSession;
    }

    constructor(props) {
        super(props);
        
        this.state = {
            tokenAPI: undefined,
            isTransacting: false,
            isSetUpBridgeModalShown: false,
            isSetTokenIconModalShown: false,
            trxHash: undefined
        };

        this.issueTokenFormSubmitted = this.issueTokenFormSubmitted.bind(this);
        this.hideErrorMessage = this.hideErrorMessage.bind(this);
        this.hideSuccessMessage = this.hideSuccessMessage.bind(this);
    };

    // Action
    issueTokenFormSubmitted(event) {
        event.preventDefault();
        this.setState({isTransacting: true});

        const form = event.target;
        const units = this.parseAmountUnits(form['amount'].value.trim());
        const token = this.sharedState.token;

        this.state.tokenAPI.issueToken(units, token)
            .then(trxHash => {
                token.increaseSupply(units);
                this.showSuccessMessage(trxHash);
                this.updateSharedState({token});
            })
            .catch(error => {
                // The issuing failed very likely due to a fact that this is
                // a 3rd party token without ability to mint tokens (the action is missing)
                if (this.isRunningOnEVM && !token.maxSupply) {
                    this.showErrorMessage(new Error('Are you sure the `mint` action is supported and you have permission to use it?'));
                    return;
                }

                this.showErrorMessage(error)
            })
            .finally(() => this.setState({isTransacting: false}));
    };

    // Components
    setTokenIconModal() {
        return (
            <Modal show={this.state.isSetTokenIconModalShown} size="sm" contentClassName="modal-content-dark" backdrop="static" centered>
                <Modal.Header>
                    <Modal.Title>Set Token Icon</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div style={{textAlign: 'center'}}>
                        The token's icon can be set by creating a pull request in the following <a href="https://github.com/telosnetwork/token-list?tab=readme-ov-file#add-token" target="_blank" rel="noreferrer" onClick={() => this.setSetTokenIconModalShown(false)}>repository</a>. Follow the linked instructions for more informations. 
                    </div>
                </Modal.Body>
        
                <Modal.Footer>
                    <Button variant="primary" onClick={() => this.setSetTokenIconModalShown(false)}>OK</Button>
                </Modal.Footer>
            </Modal>
        );
    };

    // Error Message
    showErrorMessage(error) {
        if (error.message.includes('Modal closed') || error.code === 'ACTION_REJECTED') {
            return;
        }

        const internalError = error.info?.error;
        const errorMessage = (internalError?.data?.message || internalError?.message || error.message);
        
        this.setState({errorMessage: errorMessage});
    };

    hideErrorMessage() {
        this.setState({errorMessage: undefined});
    };

    // Success Message
    showSuccessMessage(trxHash) {
        if (!this.isRunningOnEVM) {
            this.setState({trxHash});
        }
    };

    hideSuccessMessage() {
        this.setState({trxHash: undefined});
    };

    // Misc
    setSetUpBridgeModalShown(isShown) {
        this.setState({isSetUpBridgeModalShown: isShown});
    };

    setSetTokenIconModalShown(isShown) {
        this.setState({isSetTokenIconModalShown: isShown});
    };

    parseAmountUnits(amountString) {
        // If not "sanitized" the `parseUnits` may fail in case if
        // the string contains more decimal places than the token has
        const sanitizedAmount = this.sanitizeAmount(amountString);
        const parsedUnits = parseUnits(sanitizedAmount, this.sharedState.token.precision);
        return parsedUnits;
    };

    sanitizeAmount(amountString) {
        // Make sure the inputed string does not contain
        // more decimals than the token may have
        const match = amountString.match(/^(\d*)([.,]?)(\d*)?$/);
        const separator = match?.[2];

        if (!separator) {
            return amountString;
        }

        const decimals = match[3];
        const precision = Number(this.sharedState.token.precision); // BigInt otherwise

        if (decimals.length <= precision) {
            return amountString;
        }

        const wholeNumber = match[1];
        const trimedDecimalNumber = decimals.slice(0, precision);

        return (wholeNumber + '.' + trimedDecimalNumber);
    };

    // React
    componentDidMount() {
        let tokenAPI;

        if (this.isRunningOnEVM) {
            tokenAPI = new EVMTokenAPI(this.sharedState.evmSession);
        }
        else {
            tokenAPI = new AntelopeTokenAPI(this.sharedState.antelopeSession);
        }
        
        this.setState({tokenAPI});
    };

    render() {
        const token = this.sharedState.token;
        const isTransacting = this.state.isTransacting;

        return (
            <>
                {/* ToastMessage */}
                <ToastMessage.Error
                    isShown={!!this.state.errorMessage}
                    message={this.state.errorMessage}
                    onAutoClosed={this.hideErrorMessage}
                />
                
                <ToastMessage.TransactionSigned
                    isShown={!!this.state.trxHash}
                    trxHash={this.state.trxHash}
                    onAutoClosed={this.hideSuccessMessage}
                />

                {/* Modals */}
                <SetUpBridgeModal
                    isShown={this.state.isSetUpBridgeModalShown}
                    onCloseClicked={() => this.setSetUpBridgeModalShown(false)}
                />

                {this.setTokenIconModal()}

                {/* Main Content */}
                <header>
                    <h2>Issue {token.symbol}</h2>
                    <h6 className="FormHeaderSubtitle">Created token can be issued in optional amounts</h6>
                </header>

                <Stack className="IssueTokenForm-contentStack">
                    <Form id="issueTokenForm" onSubmit={this.issueTokenFormSubmitted}>
                        <Form.Group className="mb-3">
                            <Form.Label>Token's Symbol</Form.Label>
                            <Form.Control type="text" value={token.symbol} disabled={true}/>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="amount">
                            <Form.Label>Amount</Form.Label>
                            <Form.Control type="number" step="any" placeholder="Amount To Issue" disabled={isTransacting} autoFocus required/>

                            <Stack>
                                <Form.Text className="text-muted">
                                    Issued tokens will be deposited into your wallet.
                                </Form.Text>
                                
                                <Form.Text className="text-muted mt-2">
                                    Current Supply: {token.supplyFormatted}
                                </Form.Text>

                                { token.maxSupply &&
                                    <Form.Text className="text-muted mt-0">
                                        Max Supply: {token.maxSupplyFormatted}
                                    </Form.Text>
                                }

                                <Form.Text className="text-muted mt-0 pb-2">
                                    Token Contract: {token.contractAddress}
                                </Form.Text>

                                { this.isRunningOnEVM &&
                                    <Form.Text className="text-muted blue pointerCursor" onClick={() => this.setSetUpBridgeModalShown(true)}>
                                        Make the token bridgeable
                                    </Form.Text>
                                }
                                
                                <Form.Text className="text-muted blue pointerCursor" onClick={() => this.setSetTokenIconModalShown(true)}>
                                    Set the token's icon
                                </Form.Text>
                            </Stack>
                        </Form.Group>
                    </Form>

                    <LoadingButton className="actionButton" type="submit" form="issueTokenForm" variant="primary" isLoading={isTransacting}>
                        Issue
                    </LoadingButton>

                    <Button style={{marginTop: '1rem'}} variant="secondary" onClick={this.logout} disabled={isTransacting}>
                        Logout
                    </Button>
                </Stack>
            </>
        );
    };
}

export default commonContextsWrapper(IssueTokenForm);
